import {useEffect, useState} from "react";
import {Button} from "../../../basic/button";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../../../basic/dialog";
import {Copy, User} from "lucide-react";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {UserForm} from "./UserForm";
import type {EditedUserData} from "./UserForm";
import {LicenseManager} from "./LicenseManager";
import {
    useRemoveTeamMemberLicenseMutation,
    useUpdateUserByIdMutation,
    useUpdateUserEmailByIdMutation,
    VwpUser,
    VwpUserUpdateRequest
} from "../../../../store/generated/api.ts";
import {getDisplayName} from "../../../../utils/nameUtils";
import {formatDate} from "../../../../utils/dateUtils";
import {getTeamDisplayName} from "../../../../utils/teamUtils";

interface EditUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedUser: VwpUser | null;
}

type ExtendedVwpUserUpdateRequest = VwpUserUpdateRequest & {
    internalId?: string;
    flags?: EditedUserData["flags"];
};

export function EditUserDialog({open, onOpenChange, selectedUser}: EditUserDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [editedUserData, setEditedUserData] = useState<EditedUserData>({});
    const [updateUser, {isLoading}] = useUpdateUserByIdMutation();
    const [updateUserEmail, {isLoading: isUpdatingEmail}] = useUpdateUserEmailByIdMutation();
    const [removeTeamMemberLicense] = useRemoveTeamMemberLicenseMutation();

    useEffect(() => {
        if (open && selectedUser) {
            setEditedUserData({});
        }
    }, [open, selectedUser]);

    const handleSaveUserChanges = async () => {
        if (!selectedUser) return;

        // Validate required fields
        const firstName = editedUserData.firstName ?? selectedUser?.firstName;
        const lastName = editedUserData.lastName ?? selectedUser?.lastName;
        const email = editedUserData.email ?? selectedUser.email;

        if (!firstName?.trim() || !lastName?.trim() || !email?.trim()) {
            showErrorToast({message: t('common.error.nameEmailRequired')});
            return;
        }

        // email changes go through the dedicated email endpoint
        const updateRequest: ExtendedVwpUserUpdateRequest = {};

        if (editedUserData.firstName !== undefined) {
            updateRequest.firstName = editedUserData.firstName;
        }
        if (editedUserData.lastName !== undefined) {
            updateRequest.lastName = editedUserData.lastName;
        }
        if (editedUserData.role !== undefined) {
            updateRequest.role = editedUserData.role;
        }
        if (editedUserData.status !== undefined) {
            updateRequest.status = editedUserData.status;
        }
        if (editedUserData.internalId !== undefined) {
            updateRequest.internalId = editedUserData.internalId;
        }
        if (editedUserData.flags !== undefined) {
            updateRequest.flags = editedUserData.flags;
        }
        const emailChanged = Boolean(editedUserData.email && editedUserData.email !== selectedUser.email);

        try {
            if (Object.keys(updateRequest).length > 0) {
                await updateUser({
                    id: selectedUser.id,
                    vwpUserUpdateRequest: updateRequest
                }).unwrap();
            }
            if (emailChanged) {
                await updateUserEmail({
                    id: selectedUser.id,
                    vwpUserEmailChangeRequest: {email: editedUserData.email}
                }).unwrap();
            }

            const firstName = editedUserData.firstName || selectedUser?.firstName;
            const lastName = editedUserData.lastName || selectedUser?.lastName;
            const fullName = getDisplayName({
                firstName: firstName?.trim(),
                lastName: lastName?.trim()
            });

            toast.success(t('admin.users.updateSuccess', {name: fullName}));
            onOpenChange(false);
            setEditedUserData({});
        } catch (err) {
            handleApiError(err, {fallbackMessage: t('common.error.updateFailed')});
        }
    };

    const handleCancel = () => {
        onOpenChange(false);
        setEditedUserData({});
    };

    const handleRemoveLicense = async (licenseId: string) => {
        if (!selectedUser || !selectedUser.team) return;

        try {
            await removeTeamMemberLicense({
                teamId: selectedUser.team.id,
                memberEmail: selectedUser.email,
                licenseId
            }).unwrap();
            toast.success(t('admin.teams.teamManagement.licenseRemoved', {
                licenseId,
                memberName: getDisplayName(selectedUser)
            }));
        } catch (err) {
            handleApiError(err, {fallbackMessage: t('common.error.removeFailed')});
        }
    };

    const handleCopyTeamId = () => {
        if (selectedUser?.team?.id) {
            navigator.clipboard.writeText(selectedUser.team.id);
            toast.success(t('support.teamIdCopied'));
        }
    };

    const handleCopyUserId = () => {
        if (selectedUser?.id) {
            navigator.clipboard.writeText(String(selectedUser.id));
            toast.success(t('admin.users.editUser.userIdCopied'));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[90vw] max-w-7xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('admin.users.editUser.title')}</DialogTitle>
                    <DialogDescription>
                        {t('admin.users.editUser.description')}
                    </DialogDescription>
                </DialogHeader>

                {selectedUser && (
                    <div className="flex-1 overflow-y-auto space-y-6 py-4">
                        <div className="flex items-center gap-3 pb-4 border-b">
                            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-6 w-6 text-primary"/>
                            </div>
                            <div>
                                <p className="text-muted-foreground">
                                    {t('admin.users.editUser.userId')}: {selectedUser.id}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        type="button"
                                        onClick={handleCopyUserId}
                                        className="h-5 w-5 ml-1"
                                        title={t('admin.users.editUser.userIdCopied')}
                                    >
                                        <Copy className="h-3.5 w-3.5"/>
                                    </Button>
                                </p>
                                <p className="text-muted-foreground">
                                    {t('admin.users.editUser.team')}: {selectedUser.team ? (
                                    <span>
                                            {getTeamDisplayName({
                                                teamName: selectedUser.team.name,
                                                firstName: selectedUser.firstName,
                                                lastName: selectedUser.lastName
                                            }) ?? t('common.noTeam')}
                                        {selectedUser.team.id && (
                                            <span className="text-muted-foreground/60 ml-2">
                                                ({selectedUser.team.id}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    type="button"
                                                    onClick={handleCopyTeamId}
                                                    className="h-5 w-5 ml-1"
                                                    title={t('support.teamIdCopied')}
                                                >
                                                    <Copy className="h-3.5 w-3.5"/>
                                                </Button>
                                                )
                                            </span>
                                        )}
                                        </span>
                                ) : t('common.noTeam')}
                                </p>
                            </div>
                        </div>

                        <UserForm
                            selectedUser={selectedUser}
                            editedUserData={editedUserData}
                            setEditedUserData={setEditedUserData}
                        />

                        <LicenseManager selectedUser={selectedUser} onRemoveLicense={handleRemoveLicense}/>

                        <div className="pt-4 border-t space-y-1">
                            <p className="text-muted-foreground">
                                {t('admin.users.editUser.memberSince')}: {formatDate(selectedUser.joinedDate)}
                            </p>
                        </div>
                    </div>
                )}

                <DialogFooter className="flex-shrink-0">
                    <Button variant="outline" onClick={handleCancel}>
                        {t('common.action.cancel')}
                    </Button>
                    <Button onClick={handleSaveUserChanges} disabled={isLoading || isUpdatingEmail}>
                        {isLoading || isUpdatingEmail ? t('common.action.saving') : t('common.action.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}